import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/UI/Button';
import { apiErrorMessage } from '@/utils/apiError';
import { humanize } from '@/components/Workflow/workflowSettingsShared';
import {
  useDecideApprovalMutation,
  type ApprovalRequestOut,
} from '@/redux/internal-services/workflowEngineApi';

interface Props {
  approval: ApprovalRequestOut;
  onOpenEngagement?: (engagementId: string) => void;
}

const STATUS_TAG: Record<string, string> = {
  pending: 'bg-warning/15 text-warning-text',
  approved: 'bg-success/15 text-success-text',
  rejected: 'bg-danger/15 text-danger-text',
  cancelled: 'bg-ui-hover text-ui-text2',
};

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-start justify-between gap-4 py-2.5 border-b border-ui-border last:border-b-0">
    <p className="text-micro font-semibold text-ui-text3 uppercase tracking-wider">{label}</p>
    <div className="text-sm text-ui-text text-right">{children}</div>
  </div>
);

const ApprovalDetailPanel: React.FC<Props> = ({ approval, onOpenEngagement }) => {
  const { t } = useTranslation();
  const [decideApproval, { isLoading: deciding }] = useDecideApprovalMutation();
  const [rejecting, setRejecting] = useState(false);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isPending = approval.status === 'pending';

  const approve = async () => {
    setError(null);
    try {
      await decideApproval({ approvalId: approval.approval_request_id, decision: 'approved' }).unwrap();
    } catch (e) {
      setError(apiErrorMessage(e, t, t('workflow.decisionFailed', 'Could not record that decision.')));
    }
  };

  const reject = async () => {
    setError(null);
    try {
      await decideApproval({
        approvalId: approval.approval_request_id, decision: 'rejected', comments: comment.trim() || undefined,
      }).unwrap();
      setRejecting(false);
    } catch (e) {
      setError(apiErrorMessage(e, t, t('workflow.decisionFailed', 'Could not record that decision.')));
    }
  };

  return (
    <div className="rounded-xl border border-ui-border bg-ui-card p-4 sm:p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-h3 text-ui-text">{humanize(approval.approval_type)}</h1>
          <p className="mt-0.5 text-xs text-ui-text3">
            {t('workflow.approvalRequestId', 'Approval')} · {approval.approval_request_id}
          </p>
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-1 text-micro font-semibold uppercase tracking-wide ${STATUS_TAG[approval.status] ?? STATUS_TAG.pending}`}>
          {approval.status}
        </span>
      </div>

      <div className="mt-4">
        <DetailRow label={t('workflow.strategy', 'Strategy')}>{humanize(approval.approval_strategy)}</DetailRow>
        <DetailRow label={t('workflow.requestedAt', 'Requested')}>{new Date(approval.requested_at).toLocaleString()}</DetailRow>
        {approval.allowed_approver_roles.length > 0 && (
          <DetailRow label={t('workflow.allowedRoles', 'Allowed roles')}>
            {approval.allowed_approver_roles.map(humanize).join(', ')}
          </DetailRow>
        )}
        <DetailRow label={t('workflow.engagement', 'Engagement')}>
          <div className="flex items-center justify-end gap-2">
            <span className="truncate max-w-[140px] text-ui-text3 text-xs">{approval.engagement_id}</span>
            {onOpenEngagement && approval.engagement_id && (
              <button
                type="button"
                onClick={() => onOpenEngagement(approval.engagement_id)}
                className="shrink-0 text-xs font-medium text-accent-text hover:underline"
              >
                {t('workflow.openEngagement', 'Open')}
              </button>
            )}
          </div>
        </DetailRow>
        {!isPending && (
          <>
            <DetailRow label={t('workflow.decidedAt', 'Decided')}>
              {approval.decided_at ? new Date(approval.decided_at).toLocaleString() : '—'}
            </DetailRow>
            {approval.decision_comments && (
              <DetailRow label={t('workflow.comments', 'Comments')}>{approval.decision_comments}</DetailRow>
            )}
          </>
        )}
      </div>

      {isPending && (
        <div className="mt-4">
          {rejecting ? (
            <div className="space-y-2">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t('workflow.rejectCommentPlaceholder', 'Reason (optional)')}
                rows={3}
                className="w-full rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-sm text-ui-text focus:border-ui-ring focus:outline-none focus:ring-1 focus:ring-ui-ring"
              />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => { setRejecting(false); setComment(''); }}>
                  {t('workflow.cancel', 'Cancel')}
                </Button>
                <Button variant="destructive" size="sm" loading={deciding} onClick={reject}>
                  {t('workflow.confirmReject', 'Confirm reject')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" loading={deciding} onClick={() => setRejecting(true)}>
                {t('workflow.reject', 'Reject')}
              </Button>
              <Button size="sm" loading={deciding} onClick={approve}>
                {t('workflow.approve', 'Approve')}
              </Button>
            </div>
          )}
          {error && (
            <div className="mt-2 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger-text ">
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ApprovalDetailPanel;
